'use client';

import { Center, Table as ChakraTable, EmptyState, Spinner } from '@chakra-ui/react';
import type { TableCellProps, TableColumnHeaderProps, TableRootProps } from '@chakra-ui/react';

import { HiColorSwatch } from 'react-icons/hi';
import type { PropsWithChildren } from 'react';

export const DataTableLoadingState = ({ ...other }: TableCellProps) => {
	return (
		<ChakraTable.Row>
			<ChakraTable.Cell {...other}>
				<Center py={'48px'}>
					<Spinner size={'lg'} />
				</Center>
			</ChakraTable.Cell>
		</ChakraTable.Row>
	);
};

export const DataTableEmptyState = ({ ...other }: TableCellProps) => {
	return (
		<ChakraTable.Row>
			<ChakraTable.Cell {...other}>
				<EmptyState.Root>
					<EmptyState.Indicator>
						<HiColorSwatch />
					</EmptyState.Indicator>
					<EmptyState.Content>
						<EmptyState.Title>
							No result found
						</EmptyState.Title>
						<EmptyState.Description>
							Refresh or try again with different search
						</EmptyState.Description>
					</EmptyState.Content>
				</EmptyState.Root>
			</ChakraTable.Cell>
		</ChakraTable.Row>
	);
};

export type DataTableStateProps = {
  isEmpty:    boolean;
  isLoading?: boolean;
  colSpan?:   number;
};

export const DataTableState = ({ 
  isLoading, isEmpty, colSpan 
}: DataTableStateProps) => {
  if (isLoading) return <DataTableLoadingState colSpan={colSpan} />
  
  if (isEmpty) return <DataTableEmptyState colSpan={colSpan}  />

  return null;
};

export type DataTableHeader = {
  key:    string;
  column: React.ReactNode;
  style?: TableColumnHeaderProps;
};

export type DataTableProps = PropsWithChildren & TableRootProps & {
  isLoading?: boolean;
  headers:    DataTableHeader[];
  rowCount:   number | undefined;
};

export const DataTable = ({
  children, isLoading, headers, rowCount, ...tableRootProps
}: DataTableProps) => {
  const isEmpty = !rowCount;
  const contentPending = isLoading || isEmpty;

  return (
    <ChakraTable.Root stickyHeader {...tableRootProps}>
      <ChakraTable.Header>
        <ChakraTable.Row>
          {headers.map(header => (
            <ChakraTable.ColumnHeader
              key={header.key}
              color={'black'}
              {...header.style}
            >
              {header.column}
            </ChakraTable.ColumnHeader>
          ))}
        </ChakraTable.Row>
      </ChakraTable.Header>
      <ChakraTable.Body>
        {contentPending
          ? (
            <DataTableState
              colSpan={headers.length}
              isEmpty={isEmpty}
              isLoading={isLoading}
            />
          )
          : children}
      </ChakraTable.Body>
    </ChakraTable.Root>
  );
};

DataTable.Row = ChakraTable.Row;
DataTable.Cell = ChakraTable.Cell;